"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tag = exports.TagList = exports.TagGroup = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../theme");
/**
 * A tag group component that manages a collection of selectable tags.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/TagGroup.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-taggroup--default}
 *
 * @example
 * ```tsx
 * <TagGroup
 *   selectionMode="multiple"
 *   selectedKeys={selectedTags}
 *   onSelectionChange={setSelectedTags}
 * >
 *   <TagList>
 *     <Tag id="tag1">Tag 1</Tag>
 *     <Tag id="tag2">Tag 2</Tag>
 *   </TagList>
 * </TagGroup>
 * ```
 *
 * @param selectionBehavior - How multiple selection should behave in the collection
 * @param disabledKeys - The item keys that are disabled
 * @param selectionMode - The type of selection that is allowed in the collection
 * @param disallowEmptySelection - Whether the collection allows empty selection
 * @param selectedKeys - The currently selected keys in the collection (controlled)
 * @param defaultSelectedKeys - The initial selected keys in the collection (uncontrolled)
 * @param children - The children of the component
 * @param className - The CSS className for the element
 * @param style - The CSS styles for the element
 * @param onRemove - Handler that is called when a user deletes a tag
 * @param onSelectionChange - Handler that is called when the selection changes
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.TagGroup = (0, styled_1.default)(react_aria_components_1.TagGroup)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"])), theme_1.theme.space.space8);
/**
 * A list of tags that can be displayed in a row or column layout.
 * Supports empty states and dynamic collections.
 *
 * @example
 * ```tsx
 * <TagList
 *   renderEmptyState={() => <div>No tags available</div>}
 * >
 *   <Tag id="tag1">Tag 1</Tag>
 *   <Tag id="tag2">Tag 2</Tag>
 * </TagList>
 * ```
 *
 * @param renderEmptyState - Provides content to display when there are no items in the tag list
 * @param children - The contents of the collection. Can be ReactNode or a render function
 * @param dependencies - Values that should invalidate the item cache when using dynamic collections
 * @param items - Item objects in the collection
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 */
exports.TagList = (0, styled_1.default)(react_aria_components_1.TagList)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: row;\n  gap: ", ";\n"])), theme_1.theme.space.space8);
/**
 * A tag component that can be selected, disabled, or used as a link.
 * Provides visual feedback for different states (hover, focus, selected).
 *
 * @example
 * ```tsx
 * <Tag
 *   id="tag1"
 *   textValue="Tag 1"
 *   href="/tag/tag1"
 * >
 *   Tag 1
 * </Tag>
 * ```
 *
 * @param id - A unique id for the tag
 * @param textValue - A string representation of the tag's contents, used for accessibility
 * @param isDisabled - Whether the tag is disabled
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param href - A URL to link to
 * @param hrefLang - Hints at the human language of the linked URL
 * @param target - The target window for the link
 * @param rel - The relationship between the linked resource and the current page
 * @param download - Causes the browser to download the linked URL
 * @param ping - A space-separated list of URLs to ping when the link is followed
 * @param referrerPolicy - How much of the referrer to send when following the link
 * @param routerOptions - Options for the configured client side router
 * @param onHoverStart - Handler that is called when a hover interaction starts
 * @param onHoverEnd - Handler that is called when a hover interaction ends
 * @param onHoverChange - Handler that is called when the hover state changes
 */
exports.Tag = (0, styled_1.default)(react_aria_components_1.Tag)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: inline-flex;\n  width: fit-content;\n  padding: ", " ", ";\n  justify-content: center;\n  align-items: center;\n  gap: ", ";\n  flex-shrink: 0;\n  flex-grow: 0;\n  border: ", " solid ", ";\n  border-radius: ", ";\n  outline: none;\n  box-sizing: border-box;\n  height: ", ";\n  font-size: ", ";\n  background: none;\n  cursor: default;\n\n  &[data-hovered] {\n    background: ", ";\n    border: ", " solid ", ";\n  }\n\n  &[data-pressed] {\n    background: ", ";\n    border: ", " solid ", ";\n  }\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-selected] {\n    border: ", " solid ", ";\n    font-weight: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"], ["\n  display: inline-flex;\n  width: fit-content;\n  padding: ", " ", ";\n  justify-content: center;\n  align-items: center;\n  gap: ", ";\n  flex-shrink: 0;\n  flex-grow: 0;\n  border: ", " solid ", ";\n  border-radius: ", ";\n  outline: none;\n  box-sizing: border-box;\n  height: ", ";\n  font-size: ", ";\n  background: none;\n  cursor: default;\n\n  &[data-hovered] {\n    background: ", ";\n    border: ", " solid ", ";\n  }\n\n  &[data-pressed] {\n    background: ", ";\n    border: ", " solid ", ";\n  }\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-selected] {\n    border: ", " solid ", ";\n    font-weight: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"])), theme_1.theme.space.space8, theme_1.theme.space.space16, theme_1.theme.space.space8, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n80, theme_1.theme.radii.circle, theme_1.theme.space.space40, theme_1.theme.fontSizes.fontsize14, theme_1.theme.colors.n60, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n90, theme_1.theme.colors.n70, theme_1.theme.borderWidths.regular, theme_1.theme.colors.n100, theme_1.theme.opacity.semiOpaque, theme_1.theme.borderWidths.thick, theme_1.theme.colors.n900, theme_1.theme.fontWeights.medium, theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.space.space2);
var templateObject_1, templateObject_2, templateObject_3;
//# sourceMappingURL=TagGroup.js.map